/**
 * `WS /v1/models/{id}/ws` as a React hook ([design-studio §10, §11.1]). The
 * design's detail lives in the query cache; the socket only keeps it fresh:
 * `hello` replaces it, a `revision` event moves the state line at once and
 * asks for the rest, and the publish events ask for it again. The client
 * sends nothing.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ASSETLIB_KEY } from "../api/assetlib";
import { ME_KEY } from "../api/me";
import { MODELS_KEY, MODEL_KEY } from "../api/models";
import type { ModelDetail, ModelEvent, ModelStatus } from "../api/types";
import { type SocketStatus, useSocket } from "../ui/useSocket";

const FINAL: ReadonlySet<ModelStatus> = new Set<ModelStatus>([
  "ok",
  "failed",
  "refused",
  "cancelled",
]);

export const modelSocketPath = (id: string) =>
  `/v1/models/${encodeURIComponent(id)}/ws`;

export function useModelSocket(id: string): {
  status: SocketStatus;
  /** The latest thinking line of the step in progress, as received. */
  thinking: string | null;
} {
  const queryClient = useQueryClient();
  const [thinking, setThinking] = useState<string | null>(null);

  const status = useSocket(modelSocketPath(id), (raw) => {
    const event = raw as ModelEvent;
    if (event.type === "hello") {
      queryClient.setQueryData(MODEL_KEY(id), event.project);
    } else if (event.type === "thinking") {
      setThinking(typeof event.text === "string" ? event.text : null);
    } else if (event.type === "revision") {
      queryClient.setQueryData<ModelDetail>(MODEL_KEY(id), (held) =>
        held?.latest && held.latest.n === event.n
          ? {
              ...held,
              latest: {
                ...held.latest,
                status: event.status,
                step: event.step,
                step_started_at: event.step_started_at,
                error_code: event.error_code,
                error_params: event.error_params,
                refusal_reason: event.refusal_reason,
              },
            }
          : held,
      );
      // The refetch brings the current step, the history and the credits.
      void queryClient.invalidateQueries({ queryKey: MODEL_KEY(id) });
      if (FINAL.has(event.status)) {
        setThinking(null);
        void queryClient.invalidateQueries({ queryKey: ME_KEY });
      }
    } else if (event.type === "published" || event.type === "error") {
      void queryClient.invalidateQueries({ queryKey: MODEL_KEY(id) });
      void queryClient.invalidateQueries({ queryKey: MODELS_KEY, exact: true });
      if (event.type === "published") {
        void queryClient.invalidateQueries({ queryKey: ASSETLIB_KEY });
      }
    } else if (event.type === "thumbnail") {
      // The object's picture follows `published`: Objects draws it in place
      // of its neutral tile.
      void queryClient.invalidateQueries({ queryKey: ASSETLIB_KEY });
    }
  });

  return { status, thinking };
}
